import { useMemo } from 'react';
import { diffSeasonFleetClasses } from '../lib/classDivergence';

const chipStyle = {
  display: 'inline-block',
  padding: '0.05rem 0.35rem',
  margin: '0 0.2rem 0.2rem 0',
  background: '#fff',
  border: '1px solid #e0c0a0',
  borderRadius: '3px',
  fontSize: '0.8rem',
};

function Chips({ classes }) {
  return classes.map((c) => (
    <span key={c.id} style={chipStyle}>
      {c.name} <span style={{ opacity: 0.6 }}>({c.id})</span>
    </span>
  ));
}

/**
 * Surfaces (and lets the admin fix) divergence between a season's registration
 * classes and the club fleet config the schedule references (ra-gbrd.11).
 *
 * Presentational: the parent owns the data and the writes; this component just
 * computes the diff from the props and wires the buttons. Keeping it pure makes
 * it renderable in isolation (tests + screenshots).
 */
export default function ClassDivergencePanel({
  events,
  selectedSeasonId,
  onSelectSeason,
  fleetClasses,
  onPropagateToSeason,
  onPropagateToFleet,
  onSyncClassOrder,
  propagating,
  syncing,
  dirty,
  syncResult,
}) {
  const selectedSeason = useMemo(
    () => events.find((ev) => ev.id === selectedSeasonId) || null,
    [events, selectedSeasonId],
  );

  // Computed against the live (possibly unsaved) fleet classes so staging a
  // propagation updates the diff immediately.
  const divergence = useMemo(
    () => diffSeasonFleetClasses(selectedSeason?.classes, fleetClasses),
    [selectedSeason, fleetClasses],
  );

  return (
    <section className="config-section">
      <h2>Season &harr; class sync</h2>
      <p className="section-hint">
        The app offers a season&rsquo;s classes for registration; the race schedule
        references the classes configured here. When they diverge, registered boats
        can match no start. Pick a season to check, then propagate either way.
      </p>

      <label className="form-field-inline">
        <span className="form-label">Season:</span>
        <select value={selectedSeasonId} onChange={(e) => onSelectSeason(e.target.value)}>
          {events.length === 0 && <option value="">No seasons</option>}
          {events.map((ev) => (
            <option key={ev.id} value={ev.id}>{ev.name || ev.id}</option>
          ))}
        </select>
      </label>

      {selectedSeasonId && (
        <div
          className="notice"
          style={{
            marginTop: '0.6rem',
            fontSize: '0.85rem',
            borderLeft: `3px solid ${divergence.inSync ? '#27ae60' : '#e67e22'}`,
          }}
        >
          {divergence.inSync ? (
            <span>
              <span style={{ color: '#27ae60' }}>&#10003; In sync</span> — every class in{' '}
              <code>{selectedSeasonId}</code> has matching fleet-config racing details.
            </span>
          ) : (
            <>
              <strong style={{ color: '#e67e22' }}>&#9888; Classes diverge for {selectedSeasonId}.</strong>
              {divergence.onlyInSeason.length > 0 && (
                <div style={{ marginTop: '0.4rem' }}>
                  <div>
                    In the season but <strong>missing racing details here</strong> (registered boats
                    would match no start): <Chips classes={divergence.onlyInSeason} />
                  </div>
                  <button
                    className="btn btn-sm"
                    style={{ marginTop: '0.3rem' }}
                    onClick={() => onPropagateToFleet(divergence.onlyInSeason)}
                    title="Add these classes to the fleet config so you can set their flag, fleet and start"
                  >
                    Add {divergence.onlyInSeason.length} to fleet config
                  </button>
                </div>
              )}
              {divergence.onlyInFleet.length > 0 && (
                <div style={{ marginTop: '0.4rem' }}>
                  <div>
                    Configured here but <strong>not offered for registration</strong> in the
                    season: <Chips classes={divergence.onlyInFleet} />
                  </div>
                  <button
                    className="btn btn-sm"
                    style={{ marginTop: '0.3rem' }}
                    onClick={() => onPropagateToSeason(divergence.onlyInFleet)}
                    disabled={propagating || dirty}
                    title={dirty
                      ? 'Save fleet changes first — this writes the season directly, so it must propagate persisted classes only'
                      : `Add these classes to ${selectedSeasonId} so the app offers them for registration`}
                  >
                    {propagating ? 'Adding...' : `Add ${divergence.onlyInFleet.length} to season`}
                  </button>
                  {dirty && (
                    <span style={{ marginLeft: '0.4rem', fontSize: '0.75rem', color: '#888' }}>
                      Save fleet changes first.
                    </span>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      <div style={{ marginTop: '0.6rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <button
          className="btn btn-sm"
          onClick={onSyncClassOrder}
          disabled={syncing || dirty || !selectedSeasonId}
          title={dirty ? 'Save changes first' : `Rewrite class array on ${selectedSeasonId || 'selected season'} to match this order`}
        >
          {syncing ? 'Syncing...' : 'Sync class order'}
        </button>
        <span style={{ fontSize: '0.75rem', color: '#888' }}>
          Reorders matching classes in the season to follow the order set above.
        </span>
      </div>

      {syncResult && <p style={{ marginTop: '0.4rem', color: '#27ae60', fontSize: '0.85rem' }}>{syncResult}</p>}
    </section>
  );
}
